import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchInviteOrganizationHint,
  parseInviteLoginContext,
  pickPreferredOrganizationId,
} from '../utils/inviteRegistration';

/**
 * Shared multi-organization login step for product apps.
 * When the same email belongs to multiple orgs, shows org picker after password verification.
 * Pre-selects the inviting org when signing in from an invite link.
 */
export function useMultiOrgLogin({ login, completeLogin, getApiBaseUrl }) {
  const [searchParams] = useSearchParams();
  const inviteContext = parseInviteLoginContext(searchParams);

  const [orgSelection, setOrgSelection] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [inviteOrgHint, setInviteOrgHint] = useState(null);

  useEffect(() => {
    if (!inviteContext.inviteToken) {
      setInviteOrgHint(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const hint = await fetchInviteOrganizationHint(getApiBaseUrl(), inviteContext.inviteToken);
      if (!cancelled) {
        setInviteOrgHint(hint);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteContext.inviteToken, getApiBaseUrl]);

  const showOrgDropdown = orgSelection?.organizations?.length > 0;

  const beginOrganizationSelection = useCallback((result) => {
    const organizations = result.organizations || [];
    setOrgSelection({
      organizations,
      loginToken: result.loginToken,
    });
    setSelectedOrgId(pickPreferredOrganizationId(organizations, inviteOrgHint?.id));
  }, [inviteOrgHint?.id]);

  const resetOrganizationSelection = useCallback(() => {
    setOrgSelection(null);
    setSelectedOrgId('');
  }, []);

  const runCredentialLogin = useCallback(async (credentials) => {
    const result = await login({
      ...credentials,
      ...(inviteContext.inviteToken ? { invite_token: inviteContext.inviteToken } : {}),
    });
    if (result?.success && result.requiresOrganizationSelection) {
      beginOrganizationSelection(result);
      return { handled: true, needsOrgSelection: true };
    }
    return { handled: false, result };
  }, [beginOrganizationSelection, inviteContext.inviteToken, login]);

  const runOrganizationLogin = useCallback(async () => {
    if (!orgSelection?.loginToken || !selectedOrgId) {
      throw new Error('Please select an organization.');
    }
    await completeLogin(orgSelection.loginToken, selectedOrgId);
    return { success: true };
  }, [completeLogin, orgSelection?.loginToken, selectedOrgId]);

  const loginSubtitle = showOrgDropdown
    ? inviteContext.isInviteLogin && inviteOrgHint?.name
      ? `Your account belongs to multiple organizations. Select "${inviteOrgHint.name}" to accept this invitation, or choose another.`
      : 'Your email is associated with multiple organizations. Choose which one to sign in to.'
    : inviteContext.isInviteLogin
      ? 'Sign in to accept your team invitation'
      : 'Enter your credentials to access your account';

  return {
    inviteContext,
    inviteOrgHint,
    showOrgDropdown,
    orgSelection,
    selectedOrgId,
    setSelectedOrgId,
    loginSubtitle,
    beginOrganizationSelection,
    resetOrganizationSelection,
    runCredentialLogin,
    runOrganizationLogin,
  };
}
